<template>
  <div>
    <loading :active.sync="loader" :can-cancel="false" :is-full-page="true" />
    <b-modal v-model="modalVisible" size="lg" hide-header hide-footer no-enforce-focus no-close-on-backdrop no-close-on-esc>
      <b-row class="modal-header-carga">
        <b-col cols="10" class="header-left">
          <b-row>
            <b-col cols="2">
              <img alt="icono" :src="require('@/assets/ImgLogo_azul.png')">
            </b-col>
            <b-col cols="10">
              <h5 class="mb-0 titulo-modal">Carga masiva de horarios</h5>
              <small class="subtitulo-modal">Actualización de horarios por clínica y día</small>
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="2" class="text-right">
          <b-button variant="link" class="btn-cerrar-modal" @click="cerrarModal">
            <span style="font-size: 24px;">&times;</span>
          </b-button>
        </b-col>
      </b-row>
      <b-row class="modal-body-carga">
        <b-col cols="12">
          <b-tabs content-class="mt-3" v-model="tabIndex">
            <b-tab title="Cargar archivos">
              <TabCargarArchivos
                :archivo.sync="archivo"
                :archivo-valido="archivoValido"
                @descargarLayout="handleDescargarLayout"
                @cancelar="cerrarModal"
                @procesar="procesarArchivo"
              />
            </b-tab>
            <b-tab title="Resultados">
              <TabResultados
                :total-filas="totalFilas"
                :filas-actualizadas="filasActualizadas"
                :filas-con-error="filasConError"
                :errores="errores"
                @descargarErrores="descargarErrores"
                @finalizar="finalizar"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import XLSX from 'xlsx';
import TabCargarArchivos from './TabCargarArchivos/TabCargarArchivos.vue';
import TabResultados from './TabResultados/TabResultados.vue';

export default {
  components: {
    TabCargarArchivos,
    TabResultados
  },
  data() {
    return {
      loader: false,
      modalVisible: false,
      archivo: null,
      totalFilas: 0,
      filasActualizadas: 0,
      filasConError: 0,
      errores: [],
      tabIndex: 0,
      datosProcesados: null,
      archivoValido: false,
      validacionAutomatica: true
    }
  },
  watch: {
    archivo(newFile) {
      if (newFile && this.validacionAutomatica) {
        this.validarArchivoAutomaticamente();
      } else if (!newFile) {
        this.archivoValido = false;
      }
    }
  },
  methods: {
    // ==================================================================================
    // BLOQUE 1: GESTIÓN DEL MODAL
    // ==================================================================================
    abrirModal() {
      this.modalVisible = true;
      this.resetearEstado();
    },
    cerrarModal() {
      this.modalVisible = false;
      this.resetearEstado();
    },
    resetearEstado() {
      this.archivo = null;
      this.totalFilas = 0;
      this.filasActualizadas = 0;
      this.filasConError = 0;
      this.errores = [];
      this.tabIndex = 0;
      this.datosProcesados = null;
      this.archivoValido = false;
      this.validacionAutomatica = true;
    },
    finalizar() {
      this.$emit('finalizar');
      this.cerrarModal();
    },
    handleDescargarLayout() {
      const link = document.createElement('a');
      link.href = '/files/layout-carga-horarios-clínica.xlsx';
      link.download = 'layout-carga-horarios-clínica.xlsx';
      link.click();
    },

    async validarArchivoAutomaticamente() {
      this.validacionAutomatica = false;
      this.archivoValido = false;

      try {
        const data = await this.archivo.arrayBuffer();
        const jsonData = this.leerExcel(data);

        if (!this.validarContenidoParaAutoValidacion(jsonData)) return;

        const { columnasFaltantes } = this.validarColumnas(jsonData[0]);

        if (columnasFaltantes.length > 0) {
          this.mostrarModalErrores(columnasFaltantes);
        } else {
          this.archivoValido = true;
          this.mostrarAlerta('success', 'Archivo válido', 'El archivo tiene el formato correcto y está listo para procesar.');
        }
      } catch {
        this.mostrarErrorLectura();
      } finally {
        this.validacionAutomatica = true;
      }
    },

    validarContenidoParaAutoValidacion(jsonData) {
      if (jsonData.length === 0) {
        this.mostrarAlerta('error', 'Archivo vacío', 'El archivo no contiene datos');
        return false;
      }

      if (jsonData.length === 1) {
        this.mostrarAlerta('warning', 'Sin registros', 'El archivo solo contiene encabezados, no hay registros para procesar.');
        return false;
      }

      return true;
    },

    // ==================================================================================
    // BLOQUE 2: LECTURA Y VALIDACIÓN DEL ARCHIVO
    // ==================================================================================
    async procesarArchivo() {
      if (!this.validarArchivoSeleccionado()) return;

      try {
        const data = await this.archivo.arrayBuffer();
        this.procesarContenidoArchivo(data);
      } catch {
        this.mostrarErrorLectura();
      }
    },

    validarArchivoSeleccionado() {
      if (!this.archivo) {
        this.mostrarAlerta('error', 'Error', 'Por favor selecciona un archivo');
        return false;
      }
      return true;
    },

    procesarContenidoArchivo(data) {
      try {
        const jsonData = this.leerExcel(data);
        if (!this.validarContenidoArchivo(jsonData)) return;

        const { columnasEncontradas, columnasFaltantes } = this.validarColumnas(jsonData[0]);

        if (columnasFaltantes.length > 0) {
          this.mostrarModalErrores(columnasFaltantes);
        } else {
          this.procesarDatos(jsonData, columnasEncontradas);
        }
      } catch (error) {
        console.error('Error al procesar el archivo:', error);
        this.mostrarErrorLectura();
      }
    },

    leerExcel(data) {
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      return XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    },

    validarContenidoArchivo(jsonData) {
      if (jsonData.length === 0) {
        this.mostrarAlerta('error', 'Archivo vacío', 'El archivo no contiene datos');
        return false;
      }

      if (jsonData.length === 1) {
        this.mostrarAlerta('warning', 'Sin registros', 'El archivo solo contiene encabezados, no hay registros para procesar.');
        return false;
      }

      return true;
    },

    validarColumnas(headers) {
      const columnasRequeridas = this.obtenerColumnasRequeridas();
      const headersUpper = headers.map(h => String(h).toUpperCase().trim());
      const columnasFaltantes = [];
      const columnasEncontradas = {};

      columnasRequeridas.forEach(col => {
        const index = headersUpper.findIndex(h => {
          return col.variaciones.some(v => {
            const variacionNormalizada = v.trim();
            return h === variacionNormalizada || h.includes(variacionNormalizada);
          });
        });

        if (index === -1) {
          columnasFaltantes.push(col.nombre);
        } else {
          columnasEncontradas[col.nombre] = index;
        }
      });

      return { columnasEncontradas, columnasFaltantes };
    },

    obtenerColumnasRequeridas() {
      return [
        { nombre: 'CLÍNICA', variaciones: ['CLINICA', 'CLÍNICA'] },
        { nombre: 'DÍA', variaciones: ['DIA', 'DÍA', 'DIA DE LA SEMANA'] },
        { nombre: 'HORARIO DE APERTURA', variaciones: ['HORARIO DE APERTURA', 'HORA DE APERTURA', 'APERTURA', 'HORA APERTURA'] },
        { nombre: 'HORARIO DE CIERRE', variaciones: ['HORARIO DE CIERRE', 'HORA DE CIERRE', 'CIERRE', 'HORA CIERRE'] }
      ];
    },

    // ==================================================================================
    // BLOQUE 3: PROCESAMIENTO DE DATOS
    // ==================================================================================
    procesarDatos(jsonData, columnasEncontradas) {
      const datos = this.extraerDatosValidos(jsonData, columnasEncontradas);

      if (!this.validarDatosProcesados(datos)) return;

      this.datosProcesados = datos;
      this.enviarAlServicio(datos);
    },

    extraerDatosValidos(jsonData, columnasEncontradas) {
      const datos = [];
      const indices = this.obtenerIndicesColumnas(columnasEncontradas);

      for (let i = 1; i < jsonData.length; i++) {
        const filaProcesada = this.procesarFila(jsonData[i], i, indices);
        if (filaProcesada) {
          datos.push(filaProcesada);
        }
      }

      return datos;
    },

    obtenerIndicesColumnas(columnasEncontradas) {
      return {
        clinica: columnasEncontradas['CLÍNICA'],
        dia: columnasEncontradas['DÍA'],
        apertura: columnasEncontradas['HORARIO DE APERTURA'],
        cierre: columnasEncontradas['HORARIO DE CIERRE']
      };
    },

    procesarFila(fila, indice, indices) {
      if (!this.filaEsValida(fila)) return null;

      const valores = this.extraerValoresFila(fila, indices);

      // Saltar fila si clínica y día están vacíos
      const clinicaVacia = !valores.clinica || String(valores.clinica).trim() === '';
      const diaVacio = !valores.dia || String(valores.dia).trim() === '';
      if (clinicaVacia && diaVacio) return null;

      return this.crearDatoFila(indice, valores);
    },

    filaEsValida(fila) {
      return fila && fila.length > 0;
    },

    extraerValoresFila(fila, indices) {
      return {
        clinica: indices.clinica === -1 ? '' : fila[indices.clinica],
        dia: indices.dia === -1 ? '' : fila[indices.dia],
        apertura: indices.apertura === -1 ? '' : fila[indices.apertura],
        cierre: indices.cierre === -1 ? '' : fila[indices.cierre]
      };
    },

    crearDatoFila(indice, valores) {
      return {
        fila: indice + 1,
        dia: this.valorNormalizado(valores.dia),
        clinica: this.valorNormalizado(valores.clinica),
        apertura: this.convertirHoraExcel(valores.apertura),
        cierre: this.convertirHoraExcel(valores.cierre)
      };
    },

    valorNormalizado(valor) {
      return valor ? String(valor).trim() : '';
    },

    convertirHoraExcel(valor) {
      if (valor === null || valor === undefined || valor === '') return '';

      if (typeof valor === 'string') {
        const match = valor.match(/^(\d{1,2}):(\d{2})/);
        if (match) return `${match[1].padStart(2, '0')}:${match[2]}`;
        return String(valor).trim();
      }

      if (typeof valor === 'number') {
        if (valor < 0 || valor >= 1) return String(valor);

        const totalMinutos = Math.round(valor * 24 * 60);
        const horas = Math.floor(totalMinutos / 60) % 24;
        const minutos = totalMinutos % 60;
        return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`;
      }

      return String(valor);
    },

    validarDatosProcesados(datos) {
      if (datos.length === 0) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Sin datos',
          text: 'El archivo no contiene registros para procesar.',
          confirmButtonColor: '#1B81AF'
        });
        return false;
      }
      return true;
    },

    // ==================================================================================
    // BLOQUE 4: ENVÍO AL SERVICIO Y RESULTADOS
    // ==================================================================================
    enviarAlServicio(datos) {
      this.loader = true;

      this.$http.post(this.$api + 'horarioCLinica/cargaMasiva', datos)
          .then((respuesta) => {
            this.loader = false;

            const res = respuesta.data.data;

            this.totalFilas = res.totalRegistros || 0;
            this.filasActualizadas = res.registrosExitosos || 0;
            this.filasConError = res.registrosConError || 0;

            this.errores = (res.resultados || [])
              .filter(item => item.exitoso === false)
              .map(item => ({
                fila: item.fila,
                motivo: item.mensaje
              }));

            this.$nextTick(() => {
              this.tabIndex = 1;
            });

            this.$swal.fire({
              icon: this.filasConError > 0 ? 'warning' : 'success',
              title: this.filasConError > 0 ? 'Carga finalizada con errores' : 'Carga exitosa',
              text: res.mensajeGeneral,
              confirmButtonColor: '#1B81AF',
            });
          })
        .catch((error) => {
          this.loader = false;
          this.$swal.fire({
            icon: 'error',
            title: 'Error al procesar',
            text: error.response?.data?.mensaje || 'Ocurrió un error al procesar el archivo',
            confirmButtonColor: '#1B81AF'
          });
        });
    },

    descargarErrores() {
      this.loader = true;
      import('xlsx').then(XLSX => {
        if (!this.datosProcesados || this.datosProcesados.length === 0) {
          const wb = XLSX.utils.book_new();
          const ws_data = [
            ['Fila', 'Motivo del error'],
            ...this.errores.map(e => [e.fila, e.motivo])
          ];
          const ws = XLSX.utils.aoa_to_sheet(ws_data);
          XLSX.utils.book_append_sheet(wb, ws, 'Errores');
          XLSX.writeFile(wb, 'errores_carga_masiva.xlsx');
          this.loader = false;
          return;
        }

        const erroresPorFila = {};
        this.errores.forEach(e => {
          erroresPorFila[e.fila] = e.motivo;
        });

        const headers = ['Clínica', 'Día', 'Horario de apertura', 'Horario de cierre', 'OBSERVACIONES / ERRORES'];
        const ws_data = [headers];

        this.datosProcesados.forEach(dato => {
          const error = erroresPorFila[dato.fila];
          if (error) {
            ws_data.push([
              dato.clinica,
              dato.dia,
              dato.apertura,
              dato.cierre,
              error
            ]);
          }
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        XLSX.utils.book_append_sheet(wb, ws, 'Registros con Error');
        XLSX.writeFile(wb, 'registros_con_errores.xlsx');
        this.loader = false;
      });
    },

    // ==================================================================================
    // BLOQUE 5: UTILIDADES DE UI (ALERTAS Y MODALES)
    // ==================================================================================
    mostrarAlerta(icono, titulo, texto) {
      this.$swal.fire({
        icon: icono,
        title: titulo,
        text: texto,
        confirmButtonColor: '#1B81AF'
      });
    },

    mostrarErrorLectura() {
      this.mostrarAlerta('error', 'Error al leer archivo', 'No se pudo leer el archivo. Asegúrate de que sea un archivo Excel válido.');
    },

    mostrarModalErrores(columnasFaltantes) {
      const listaFaltantes = columnasFaltantes.map(c => `<li class="text-left">${c}</li>`).join('');

      this.$swal.fire({
        icon: 'error',
        title: 'Formato de archivo incorrecto',
        html: `
          <div class="text-left">
            <p><strong>Faltan las siguientes columnas obligatorias:</strong></p>
            <ul style="color: #dc3545;">${listaFaltantes}</ul>
            <p class="mt-3 text-muted"><small>Descarga el layout oficial para obtener el formato correcto.</small></p>
          </div>
        `,
        width: '450px',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1B81AF'
      });
    }
  }
}
</script>

<style scoped>
.modal-header-carga {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem;
}

.header-left .row {
  align-items: center;
}

.titulo-modal {
  font-weight: 600;
  color: #333;
}

.subtitulo-modal {
  color: #666;
  font-size: 12px;
}

.btn-cerrar-modal {
  padding: 0;
  color: #666;
}

.btn-cerrar-modal:hover {
  color: #333;
}

.modal-body-carga {
  padding: 1rem;
}

/* Modal redondeado */
::v-deep .modal-content {
  border-radius: 16px;
  overflow: hidden;
}

::v-deep .modal-dialog {
  border-radius: 16px;
}
</style>